import React, { useCallback, useMemo, useState } from 'react';
import { CalculationType, CustomExportRunType } from '@ydistri/api-sdk';
import { useGetCalculationQuery } from './apiCalculationDetail';
import { useTemplateOrCalculation } from '../../hooks/useTemplateOrCalculation';
import { Button, Row } from '@ydistri/ds';
import { MdOutlineUpload } from 'react-icons/md';
import CalculationAction from '../Calculations/actions/CalculationAction';
import { useApplicationConfiguration } from '../../hooks/useApplicationConfiguration';
import { useCreateCustomExportRunMutation } from './CustomExport/apiCalculationExport';
import {
  getCustomExportAvailableActions,
  getCustomExportTooltip,
} from './CustomExport/customExportLib';
import { skipToken } from '@reduxjs/toolkit/query';
import { Dropdown, DropdownProps } from 'antd';
import { useAppDispatch } from '../../store';
import { addToast } from '@ydistri/utils';
import { useGetLatestCustomExportRun } from './CustomExport/useGetLatestCustomExportRun';
import { useNavigate } from 'react-router';
import { RoutingPage } from '../../routes/routerSlice';
import { CalculationDetailSubpages } from '../Calculations/calculationsLib';
import { useStableParams } from '../../routes/useStableParams';
import CustomExportConfirmationModal from './CustomExport/components/CustomExportConfirmationModal';

const appIcon = <MdOutlineUpload size={20} />;

const customExportActionCopy: Record<
  CustomExportRunType,
  {
    label: string;
    title: string;
    message?: string;
    confirmationButtonLabel: string;
  }
> = {
  [CustomExportRunType.New]: {
    label: 'Start custom export',
    title: 'Start custom export',
    confirmationButtonLabel: 'Continue',
  },
  [CustomExportRunType.RetryErrors]: {
    label: 'Retry failed items',
    title: 'Retry failed items',
    message: 'Create a retry run for failed custom export items?',
    confirmationButtonLabel: 'Continue',
  },
  [CustomExportRunType.SkipErrors]: {
    label: 'Skip failed items',
    title: 'Skip failed items',
    message: 'Create a run that skips failed custom export items?',
    confirmationButtonLabel: 'Continue',
  },
};

interface CustomExportButtonProps {
  variant?: 'icon' | 'button';
}

const CustomExportButton: React.FC<CustomExportButtonProps> = ({ variant = 'icon' }) => {
  const templateOrCalculation = useTemplateOrCalculation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { projectShortName } = useStableParams();
  const { data: calculationData } = useGetCalculationQuery(templateOrCalculation.id);
  const { isCustomExportEnabled } = useApplicationConfiguration();
  const [selectedAction, setSelectedAction] = useState<CustomExportRunType>();
  const [createCustomExportRun, createCustomExportRunStatus] = useCreateCustomExportRunMutation();

  const isProduction = calculationData?.type === CalculationType.Production;
  const { data: latestRun, isFetching: isLatestRunFetching } = useGetLatestCustomExportRun(
    templateOrCalculation,
    isProduction ? undefined : skipToken,
  );

  const availableActions = useMemo(
    () =>
      isCustomExportEnabled && isProduction && !isLatestRunFetching
        ? getCustomExportAvailableActions(latestRun)
        : [],
    [isCustomExportEnabled, isLatestRunFetching, isProduction, latestRun],
  );

  const openConfirmation = useCallback((action: CustomExportRunType) => {
    setSelectedAction(action);
  }, []);

  const openDefaultConfirmation = useCallback(() => {
    const action = availableActions[0];
    if (availableActions.length > 0) {
      openConfirmation(action);
    }
  }, [availableActions, openConfirmation]);

  const closeModal = useCallback(() => {
    setSelectedAction(undefined);
  }, []);

  const loading = isLatestRunFetching || createCustomExportRunStatus.isLoading;

  const navigateToCustomExport = useCallback(() => {
    if (!projectShortName) return;

    navigate(
      `/${projectShortName}/${RoutingPage.CALCULATION_DETAIL}/${templateOrCalculation.id}/${CalculationDetailSubpages.CUSTOM_EXPORT}`,
    );
  }, [navigate, projectShortName, templateOrCalculation.id]);

  const runSelectedAction = useCallback(() => {
    if (!selectedAction) return;

    void createCustomExportRun({
      calculationId: templateOrCalculation.id,
      type: selectedAction,
    })
      .unwrap()
      .then(() => {
        if (variant === 'icon') {
          navigateToCustomExport();
        }
      })
      .catch(() => {
        dispatch(
          addToast({
            message: `${customExportActionCopy[selectedAction].label} failed.`,
            isError: true,
          }),
        );
      })
      .finally(closeModal);
  }, [
    closeModal,
    createCustomExportRun,
    dispatch,
    navigateToCustomExport,
    selectedAction,
    templateOrCalculation.id,
    variant,
  ]);

  const dropdownMenu: DropdownProps['menu'] = useMemo(
    () => ({
      items: availableActions.map(action => ({
        key: action,
        label: customExportActionCopy[action].label,
        onClick: () => openConfirmation(action),
      })),
    }),
    [availableActions, openConfirmation],
  );

  const tooltip = getCustomExportTooltip(latestRun);

  if (!isCustomExportEnabled) return null;
  if (!isProduction) return null;

  const hasMultipleActions = availableActions.length > 1;
  const actionDisabled = availableActions.length === 0 || loading;
  const modalCopy = selectedAction ? customExportActionCopy[selectedAction] : undefined;

  if (variant === 'button') {
    if (availableActions.length === 0) return null;

    return (
      <>
        <Row $gap="0.5rem">
          {availableActions.map(action => (
            <Button
              key={action}
              icon={loading ? undefined : appIcon}
              loading={loading}
              disabled={loading}
              onClick={() => openConfirmation(action)}
            >
              {customExportActionCopy[action].label}...
            </Button>
          ))}
        </Row>

        {modalCopy && (
          <CustomExportConfirmationModal
            title={modalCopy.title}
            message={modalCopy.message}
            onConfirmed={runSelectedAction}
            onCanceled={closeModal}
            confirmationButtonLabel={modalCopy.confirmationButtonLabel}
            running={loading}
          />
        )}
      </>
    );
  }

  const actionButton = (
    <CalculationAction
      data-type="ProductionCalculationIcon"
      data-checked={!!latestRun}
      icon={appIcon}
      tooltipPosition="left"
      tooltip={tooltip}
      checked={!!latestRun}
      loading={loading}
      disabled={actionDisabled}
      onClick={hasMultipleActions ? undefined : openDefaultConfirmation}
    />
  );

  return (
    <>
      {hasMultipleActions ? (
        <Dropdown trigger={['click']} menu={dropdownMenu} disabled={actionDisabled}>
          {actionButton}
        </Dropdown>
      ) : (
        actionButton
      )}

      {modalCopy && (
        <CustomExportConfirmationModal
          title={modalCopy.title}
          message={modalCopy.message}
          onConfirmed={runSelectedAction}
          onCanceled={closeModal}
          confirmationButtonLabel={modalCopy.confirmationButtonLabel}
          running={loading}
        />
      )}
    </>
  );
};

export default CustomExportButton;
